import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, FlaskConical, Home } from "lucide-react";

function useDarkMode() {
  const [dark, setDark] = useState(
    () => document.documentElement.getAttribute("data-theme") === "dark"
  );
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setDark(document.documentElement.getAttribute("data-theme") === "dark");
    });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return dark;
}

const DIAGNOSTICS_BENEFITS = [
  "Comprehensive Lab & Imaging Services",
  "Convenient Home Sample Collection",
  "Fast & Secure Digital Reports",
  "Integrated with Your Health Records",
];

const HOMECARE_BENEFITS = [
  "Doctor Visits at Your Doorstep",
  "Skilled Nursing & Elder Care",
  "Post-Hospitalization Recovery Support",
  "Personalized Care Plans",
];

const DIAG_STATS = [
  { value: "500+",  label: "Labs" },
  { value: "99.2%", label: "Accuracy" },
  { value: "<4h",   label: "Report Time" },
];

const HOME_STATS = [
  { value: "2K+", label: "Caregivers" },
  { value: "96%", label: "Satisfaction" },
  { value: "<2h", label: "Arrival" },
];

function StatRow({ stats, bg, numColor, lblColor, divColor }) {
  return (
    <div
      className="mt-8 rounded-2xl transition-all duration-300"
      style={{ ...bg, display: "grid", gridTemplateColumns: "repeat(3,1fr)", padding: "12px 8px" }}
    >
      {stats.map(({ value, label }, i) => (
        <div
          key={label}
          className="text-center"
          style={i > 0 ? { borderLeft: `1px solid ${divColor}` } : {}}
        >
          <p className="font-bold transition-colors duration-300"
            style={{ color: numColor, fontSize: "clamp(0.85rem,2.5vw,1.25rem)", lineHeight: 1.2 }}>
            {value}
          </p>
          <p className="mt-0.5 uppercase tracking-widest transition-colors duration-300"
            style={{ color: lblColor, fontSize: "clamp(8px,1.8vw,10px)" }}>
            {label}
          </p>
        </div>
      ))}
    </div>
  );
}

export default function ServiceCards() {
  const dark = useDarkMode();

  const diagCard = dark
    ? { background: "#110d02", border: "1px solid rgba(200,150,30,0.30)", boxShadow: "0 4px 40px rgba(200,150,30,0.07)" }
    : { background: "#ffffff", border: "1px solid #e2e8f0", boxShadow: "0 2px 12px rgba(3,45,87,0.06)" };

  const homeCard = dark
    ? { background: "linear-gradient(135deg, #1a1200 0%, #0f0a00 100%)", border: "1px solid rgba(200,150,30,0.40)", boxShadow: "0 8px 48px rgba(200,150,30,0.10)" }
    : { background: "linear-gradient(135deg, #0e1f35 0%, #122d48 100%)", border: "1px solid rgba(255,255,255,0.08)" };

  const diagBadge = dark
    ? { background: "rgba(200,150,30,0.12)", border: "1px solid rgba(200,150,30,0.35)", color: "#f0c040" }
    : { background: "rgba(20,184,166,0.10)", border: "1px solid rgba(20,184,166,0.25)", color: "#0f766e" };

  const homeBadge = dark
    ? { background: "rgba(200,150,30,0.10)", border: "1px solid rgba(200,150,30,0.28)", color: "#c8961e" }
    : { background: "rgba(255,255,255,0.10)", border: "1px solid rgba(255,255,255,0.15)", color: "#5eead4" };

  const diagTitle   = dark ? "#f5e6b0" : "#0f172a";
  const diagSub     = dark ? "#b89a4a" : "#64748b";
  const diagBenefit = dark ? "#e0c880" : "#334155";
  const homeTitle   = dark ? "#f5e6b0" : "#ffffff";
  const homeSub     = dark ? "#b89a4a" : "rgba(255,255,255,0.65)";
  const homeBenefit = dark ? "#e0c880" : "rgba(255,255,255,0.85)";

  const diagCheck     = dark ? { background: "rgba(200,150,30,0.15)", border: "1px solid rgba(200,150,30,0.45)" } : { background: "#0f172a" };
  const diagCheckIcon = dark ? "#f0c040" : "#ffffff";
  const homeCheck     = dark ? { background: "#c8961e" } : { background: "#2dd4bf" };
  const homeCheckIcon = dark ? "#0a0800" : "#0f172a";

  const diagStatsBg  = dark ? { background: "rgba(200,150,30,0.05)", border: "1px solid rgba(200,150,30,0.14)" } : { background: "#f8fafc", border: "1px solid #e2e8f0" };
  const diagStatNum  = dark ? "#f5e6b0" : "#0f172a";
  const diagStatLbl  = dark ? "#7a5c10" : "#94a3b8";
  const diagStatDiv  = dark ? "rgba(200,150,30,0.15)" : "#e2e8f0";

  const homeStatsBg  = dark ? { background: "rgba(200,150,30,0.05)", border: "1px solid rgba(200,150,30,0.14)" } : { background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.10)" };
  const homeStatNum  = dark ? "#f5e6b0" : "#ffffff";
  const homeStatLbl  = dark ? "#7a5c10" : "#38bdf8";
  const homeStatDiv  = dark ? "rgba(200,150,30,0.15)" : "rgba(255,255,255,0.10)";

  return (
    <section className="relative py-16 sm:py-24 lg:py-32">
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6">

        <div className="grid sm:grid-cols-1 grid-cols-2 gap-6">

          {/* ── DIAGNOSTICS ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-[2rem] p-6 sm:p-8 lg:p-12 flex flex-col transition-all duration-300"
            style={diagCard}
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider w-fit transition-all duration-300"
              style={diagBadge}>
              <FlaskConical className="h-3.5 w-3.5" />
              Lab and Imaging Services
            </span>

            <h3 className="mt-5 font-sans text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight transition-colors duration-300"
              style={{ color: diagTitle }}>
              Trusted Diagnostics,<br />Connected Care.
            </h3>

            <p className="mt-3 text-sm leading-relaxed transition-colors duration-300"
              style={{ color: diagSub }}>
              Book tests, access reports, and keep your healthcare journey connected.
            </p>

            <ul className="mt-7 space-y-3 flex-1">
              {DIAGNOSTICS_BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm transition-colors duration-300"
                  style={{ color: diagBenefit }}>
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full transition-all duration-300"
                    style={diagCheck}>
                    <Check className="h-3 w-3" strokeWidth={3} style={{ color: diagCheckIcon }} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            <StatRow stats={DIAG_STATS} bg={diagStatsBg} numColor={diagStatNum} lblColor={diagStatLbl} divColor={diagStatDiv} />
          </motion.div>

          {/* ── HOME CARE ── */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="relative overflow-hidden rounded-[2rem] p-6 sm:p-8 lg:p-12 flex flex-col transition-all duration-300"
            style={homeCard}
          >
            <span className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wider w-fit transition-all duration-300"
              style={homeBadge}>
              <Home className="h-3.5 w-3.5" />
              Home Care
            </span>

            <h3 className="mt-5 font-sans text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight transition-colors duration-300"
              style={{ color: homeTitle }}>
              Care That<br />Comes Home.
            </h3>

            <p className="mt-3 text-sm leading-relaxed transition-colors duration-300"
              style={{ color: homeSub }}>
              Professional home healthcare services designed around your comfort and recovery.
            </p>

            <ul className="mt-7 space-y-3 flex-1">
              {HOMECARE_BENEFITS.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm transition-colors duration-300"
                  style={{ color: homeBenefit }}>
                  <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full transition-all duration-300"
                    style={homeCheck}>
                    <Check className="h-3 w-3" strokeWidth={3} style={{ color: homeCheckIcon }} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>

            <StatRow stats={HOME_STATS} bg={homeStatsBg} numColor={homeStatNum} lblColor={homeStatLbl} divColor={homeStatDiv} />
          </motion.div>

        </div>
      </div>
    </section>
  );
}